import React, {useCallback, useEffect, useRef} from 'react';

import {CommonInputInterface} from '../common-input-interface';
import {useValue, useFocus} from '../input-hooks';
import {Field} from '../field';
import {cls, useUniqueId} from '../../../utils';

import css from './text-area.module.css';

interface InputInterface extends CommonInputInterface<string> {
	setRef?: React.RefObject<HTMLTextAreaElement | null>;
	resizable?: boolean;
	autoHeightAdjustable?: boolean;
}

export type InputProps = Omit<
	InputInterface & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, keyof InputInterface>,
	'appearance' | 'size' | 'shape'
>;

export const TextArea: React.FC<InputProps> = (props) => {
	const {
		error,
		disabled,
		resizable,
		className,
		bottomText,
		autoFocus,
		label,
		helperText,
		autoHeightAdjustable,
		onChange: _,
		setRef,
		...inputProps
	} = props;
	const {hasFocus, onBlur, onFocus} = useFocus(props);
	const {value, onChange} = useValue(props);

	const generatedId = useUniqueId('Input');
	const id = props.id || generatedId;

	const tmpRef = useRef<HTMLTextAreaElement>(null);
	const inputRef = setRef || tmpRef;

	const updateHeight = useCallback(() => {
		if (autoHeightAdjustable) {
			const input = inputRef.current;
			setTimeout(() => {
				if (input) {
					input.style.height = '5px';
					input.style.height = input.scrollHeight + 'px';
				}
			});
		}
	}, [autoHeightAdjustable]);

	useEffect(() => {
		updateHeight();
	}, [updateHeight]);

	useEffect(() => {
		if (autoFocus && inputRef && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.selectionStart = inputRef.current.value.length;
		}
	}, [autoFocus]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (props.onKeyDown) props.onKeyDown(e);
			updateHeight();
		},
		[props.onKeyDown],
	);

	return (
		<Field
			commonProps={props}
			hasFocus={hasFocus}
			appearance="outline"
			topLabel={!!value || hasFocus}
			className={className}
			normalLabel
		>
			<textarea
				id={id}
				disabled={disabled}
				className={cls(css.input, hasFocus && css.hasFocus, resizable && css.resizable)}
				{...(inputProps as any)}
				onChange={onChange}
				onKeyDown={handleKeyDown}
				onBlur={onBlur}
				onFocus={onFocus}
				ref={inputRef}
				value={value || ''}
			/>
		</Field>
	);
};
